import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  zIndex?: 10 | 20 | 30 | 40 | 50;
  className?: string;
  theme?: 'dark' | 'light';
  truncate?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  zIndex = 10,
  className = '',
  theme = 'dark',
  truncate = true,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const themeClasses = {
    dark: 'bg-neutral-700 text-neutral-0',
    light: 'bg-neutral-0 text-neutral-700 shadow-lg',
  };

  const updateTooltipPosition = () => {
    if (triggerRef.current && tooltipRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const tooltipOffset = 8;

      let top = 0;
      let left = rect.left + rect.width / 2;

      switch (position) {
        case 'top':
          top = rect.top - tooltipOffset - tooltipRect.height;
          break;
        case 'bottom':
          top = rect.bottom + tooltipOffset;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - tooltipOffset - tooltipRect.width;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + tooltipOffset;
          break;
      }

      setTooltipPosition({ top, left });
    }
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);

    setTimeout(() => {
      updateTooltipPosition();
    }, 0);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  useEffect(() => {
    if (showTooltip) {
      const handleScroll = () => updateTooltipPosition();
      const handleResize = () => updateTooltipPosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showTooltip]);

  const getArrowPosition = () => {
    switch (position) {
      case 'top':
        return 'bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1';
      case 'bottom':
        return 'top-0 left-1/2 transform -translate-x-1/2 -translate-y-1';
      case 'left':
        return 'right-0 top-1/2 transform -translate-y-1/2 translate-x-1';
      case 'right':
        return 'left-0 top-1/2 transform -translate-y-1/2 -translate-x-1';
      default:
        return 'bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1';
    }
  };

  const getTransform = () => {
    switch (position) {
      case 'top':
      case 'bottom':
        return 'translateX(-50%)';
      case 'left':
      case 'right':
        return 'translateY(-50%)';
      default:
        return 'translateX(-50%)';
    }
  };

  const isDisabled = content === '';

  if (isDisabled) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={triggerRef}
        className={`inline-block ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {showTooltip &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`fixed z-${zIndex} ${themeClasses[theme]} text-xs rounded py-2 px-3 max-w-[15rem] w-max pointer-events-none transition-opacity duration-300`}
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: getTransform(),
            }}
          >
            <div className={truncate ? 'text-ellipsis line-clamp-5' : ''}>
              {content}
            </div>

            <div
              className={`absolute w-2 h-2 ${themeClasses[theme]} transform rotate-45 ${getArrowPosition()}`}
            />
          </div>,
          document.body,
        )}
    </>
  );
};
